import { FORM_OPERATION, Question } from "@archetypes/form";
import { formOperation } from "@utils/form-delta.util";
import { startAppListening } from "../listener-middleware";
import {
  addPage,
  addQuestion,
  clonePage,
  cloneQuestion,
  cloneFieldRules,
  copyQuestion,
  movePage,
  moveQuestion,
  removePage,
  removeQuestion,
  updateFieldRules,
  updatePage,
  updateQuestion,
} from "../slices/form.slice";

const getConnectedFrequencyStatQuestions = (questions: Question[], frequencyQuestionId: string) =>
  questions.filter((question) => question.content.connectedFieldId === frequencyQuestionId);

const getQuestionLocation = (
  pages: Array<{ id: string; questions: Question[] }>,
  questionId: string,
): { pageId: string; position: number; question: Question } | null => {
  for (const page of pages) {
    const position = page.questions.findIndex((question) => question.id === questionId);
    if (position !== -1) {
      return {
        pageId: page.id,
        position,
        question: page.questions[position],
      };
    }
  }

  return null;
};

const syncQuestionDelta = async ({
  appId,
  formId,
  client,
  type,
  questions,
}: {
  appId: string;
  formId: string;
  client: Parameters<typeof formOperation>[0]["client"];
  type: FORM_OPERATION;
  questions: Array<{ pageId: string; position: number; question: Question }>;
}) => {
  for (const entry of questions) {
    if (entry.position < 0) {
      continue;
    }

    await formOperation({
      appId,
      formId,
      pageId: entry.pageId,
      client,
      type,
      metadata: { position: entry.position, question: entry.question },
    });
  }
};

startAppListening({
  actionCreator: addPage,
  effect: (action, api) => {
    const { appId, formId, pageId, id, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const newPageIndex = formState.formData.pages.findIndex((p) => p.id === id);
    if (newPageIndex === -1) return;
    const newPage = formState.formData.pages[newPageIndex];
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.PAGE_BREAK,
      metadata: { position: newPageIndex, page: newPage },
    });
  },
});

startAppListening({
  actionCreator: clonePage,
  effect: (action, api) => {
    const { appId, formId, pageId, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const newPageId = formState.lastClonedQuestionOrPageId;
    const position = formState.formData.pages.findIndex((p) => p.id === newPageId);
    if (position === -1) return;
    const clonedPage = formState.formData.pages[position];
    formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.CLONE_PAGE,
      metadata: { position, page: clonedPage },
    });
  },
});

startAppListening({
  actionCreator: movePage,
  effect: (action, api) => {
    const { appId, formId, moveablePageId, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const position = formState.formData.pages.findIndex((p) => p.id === moveablePageId);
    if (position === -1) return;
    formOperation({
      appId,
      formId,
      client,
      type: FORM_OPERATION.MOVE_PAGE,
      metadata: { position, page: { id: moveablePageId } },
    });
  },
});

startAppListening({
  actionCreator: addQuestion,
  effect: async (action, api) => {
    const { appId, formId, pageId, id, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const pageIndex = formState.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const questions = formState.formData.pages[pageIndex].questions;
    const position = questions.findIndex((q) => q.id === id);
    if (position === -1) return;
    const addedQuestion = questions[position];
    const deltaQuestions = [{ pageId, position, question: addedQuestion }];

    if (addedQuestion.type === "Frequency") {
      const connectedQuestions = getConnectedFrequencyStatQuestions(
        questions,
        addedQuestion.id,
      ).map((question) => ({
        pageId,
        position: questions.findIndex((candidate) => candidate.id === question.id),
        question,
      }));
      deltaQuestions.push(...connectedQuestions);
    }

    await syncQuestionDelta({
      appId,
      formId,
      client,
      type: FORM_OPERATION.ADD_QUESTION,
      questions: deltaQuestions,
    });
  },
});

startAppListening({
  actionCreator: updatePage,
  effect: (action, api) => {
    const { appId, formId, page, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const position = formState.formData.pages.findIndex((p) => p.id === page.id);
    formOperation({
      appId,
      formId,
      client,
      type: FORM_OPERATION.UPDATE_PAGE,
      metadata: { position, page: { ...page, questions: undefined } },
    });
  },
});

startAppListening({
  actionCreator: updateQuestion,
  effect: async (action, api) => {
    const { appId, formId, pageId, questionId, question, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const pageIndex = formState.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const position = formState.formData.pages[pageIndex].questions.findIndex(
      (q) => q.id === questionId,
    );
    if (position === -1) return;
    const deltaQuestions = [{ pageId, position, question }];

    if (question.type === "Frequency") {
      const connectedQuestions = getConnectedFrequencyStatQuestions(
        formState.formData.pages.flatMap((page) => page.questions),
        question.id,
      )
        .map((connectedQuestion) =>
          getQuestionLocation(formState.formData.pages, connectedQuestion.id),
        )
        .filter(
          (value): value is { pageId: string; position: number; question: Question } => !!value,
        );

      deltaQuestions.push(...connectedQuestions);
    }

    await syncQuestionDelta({
      appId,
      formId,
      client,
      type: FORM_OPERATION.UPDATE_QUESTION,
      questions: deltaQuestions,
    });
  },
});

startAppListening({
  actionCreator: cloneQuestion,
  effect: async (action, api) => {
    const { appId, formId, pageId, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const newQuestionId = formState.lastClonedQuestionOrPageId;
    const pageIndex = formState.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const position = formState.formData.pages[pageIndex].questions.findIndex(
      (q) => q.id === newQuestionId,
    );
    if (position === -1) return;
    const question = formState.formData.pages[pageIndex].questions[position];

    await formOperation({
      appId,
      formId,
      pageId,
      client,
      type: FORM_OPERATION.CLONE_QUESTION,
      metadata: {
        question,
        position,
      },
    });

    if (question.type === "Frequency") {
      const connectedQuestions = getConnectedFrequencyStatQuestions(
        formState.formData.pages[pageIndex].questions,
        question.id,
      ).map((connectedQuestion) => ({
        pageId,
        position: formState.formData.pages[pageIndex].questions.findIndex(
          (candidate) => candidate.id === connectedQuestion.id,
        ),
        question: connectedQuestion,
      }));

      await syncQuestionDelta({
        appId,
        formId,
        client,
        type: FORM_OPERATION.ADD_QUESTION,
        questions: connectedQuestions,
      });
    }
  },
});

startAppListening({
  actionCreator: moveQuestion,
  effect: (action, api) => {
    const { appId, formId, pageId, client, currentQuestionId, targetQuestionPageId } =
      action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const targetPageIndex = formState.formData.pages.findIndex(
      (p) => p.id === targetQuestionPageId,
    );
    if (targetPageIndex === -1) return;
    const position = formState.formData.pages[targetPageIndex].questions.findIndex(
      (q) => q.id === currentQuestionId,
    );
    if (position === -1) return;
    const question: Question = JSON.parse(
      JSON.stringify(formState.formData.pages[targetPageIndex].questions[position]),
    );
    formOperation({
      appId,
      formId,
      pageId,
      targetPageId: targetQuestionPageId,
      client,
      type: FORM_OPERATION.MOVE_QUESTION,
      metadata: { question, position },
    });
  },
});

startAppListening({
  actionCreator: copyQuestion,
  effect: async (action, api) => {
    const { appId, formId, pageId, client } = action.payload;
    const formState = api.getState().form[formId];
    if (!formState) return;
    const newQuestionId = formState.lastClonedQuestionOrPageId;
    const pageIndex = formState.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const questions = formState.formData.pages[pageIndex].questions;
    const position = questions.findIndex((q) => q.id === newQuestionId);
    if (position === -1) return;
    const addedQuestion = questions[position];
    const deltaQuestions = [{ pageId, position, question: addedQuestion }];

    if (addedQuestion.type === "Frequency") {
      const connectedQuestions = getConnectedFrequencyStatQuestions(
        questions,
        addedQuestion.id,
      ).map((question) => ({
        pageId,
        position: questions.findIndex((candidate) => candidate.id === question.id),
        question,
      }));
      deltaQuestions.push(...connectedQuestions);
    }

    await syncQuestionDelta({
      appId,
      formId,
      client,
      type: FORM_OPERATION.ADD_QUESTION,
      questions: deltaQuestions,
    });
  },
});

startAppListening({
  actionCreator: removePage,
  effect: (action, api) => {
    const { appId, formId, pageId, client } = action.payload;
    const previous = api.getOriginalState().form[formId];
    if (!previous) return;
    const position = previous.formData.pages.findIndex((p) => p.id === pageId);
    if (position === -1) return;
    const page = JSON.parse(JSON.stringify(previous.formData.pages[position]));
    formOperation({
      appId,
      formId,
      client,
      type: FORM_OPERATION.REMOVE_PAGE,
      metadata: { position, page: { ...page, questions: undefined } },
    });
  },
});

startAppListening({
  actionCreator: removeQuestion,
  effect: async (action, api) => {
    const { appId, formId, pageId, questionId, client } = action.payload;
    const previous = api.getOriginalState().form[formId];
    if (!previous) return;
    const pageIndex = previous.formData.pages.findIndex((p) => p.id === pageId);
    if (pageIndex === -1) return;
    const position = previous.formData.pages[pageIndex].questions.findIndex(
      (q) => q.id === questionId,
    );
    if (position === -1) return;
    const question: Question = JSON.parse(
      JSON.stringify(previous.formData.pages[pageIndex].questions[position]),
    );
    if (question.content.connectedFieldId) {
      return;
    }
    const deltaQuestions = [{ pageId, position, question }];

    if (question.type === "Frequency") {
      const connectedQuestions = getConnectedFrequencyStatQuestions(
        previous.formData.pages.flatMap((page) => page.questions),
        question.id,
      )
        .map((connectedQuestion) =>
          getQuestionLocation(previous.formData.pages, connectedQuestion.id),
        )
        .filter(
          (value): value is { pageId: string; position: number; question: Question } => !!value,
        );

      deltaQuestions.push(...connectedQuestions);
    }

    await syncQuestionDelta({
      appId,
      formId,
      client,
      type: FORM_OPERATION.REMOVE_QUESTION,
      questions: deltaQuestions,
    });
  },
});

startAppListening({
  actionCreator: updateFieldRules,
  effect: (action) => {
    const { type, rule, appId, formId, client } = action.payload;
    const operationType =
      type === "DELETE"
        ? FORM_OPERATION.DELETE_QUESTION_CONDITION
        : type === "UPDATE"
          ? FORM_OPERATION.UPDATE_QUESTION_CONDITION
          : FORM_OPERATION.ADD_QUESTION_CONDITION;
    formOperation({
      appId,
      formId,
      pageId: rule.pageId,
      questionId: rule.questionId,
      ruleId: rule.id,
      client,
      type: operationType,
      metadata: { fieldRule: rule },
    });
  },
});

startAppListening({
  actionCreator: cloneFieldRules,
  effect: (action, api) => {
    const { appId, formId, client } = action.payload;
    const before = api.getOriginalState().form[formId]?.formData.fieldRules ?? [];
    const after = api.getState().form[formId]?.formData.fieldRules ?? [];
    const addedRules = after.slice(before.length);
    if (addedRules.length === 0) return;
    formOperation({
      appId,
      formId,
      client,
      type: FORM_OPERATION.BULK_QUESTION_CONDITION,
      metadata: { bulkFieldRules: addedRules },
    });
  },
});
